import React, { useMemo, useState } from 'react';
import {graphql, navigate, useStaticQuery} from 'gatsby';

import { filteredCaseStudies } from '@src/utils/parsers';
import { getCaseStudiesOptions } from '@src/utils/caseStudiesOptions';
import useActivePage from '@src/hooks/useActivePage';

import { SearchInput } from '@commons/search-input/SearchInput';
import { MultipleSelectInput } from '@commons/multiple-select-input/MultipleSelectInput';
import { StandardSection } from '@commons/standard-section/StandardSection';
import { CaseStudyCard } from '@commons/case-study-card/CaseStudyCard';
import PaginationControls from '@commons/pagination/PaginationControls';

import * as styles from './case-studies-section.module.scss';

const pageSize = 6;

export function CaseStudiesSection({ location }) {
  const caseStudiesQuery = useStaticQuery(graphql`
    query {
      allMarkdownRemark(
        filter: { frontmatter: { layout: { eq: "caseStudies" } } }
        sort: { fields: frontmatter___date, order: DESC }
      ) {
        edges {
          node {
            id
            frontmatter {
              path
              category
              keywords
              searchKeywords
              caseStudyId
              description
              serviceType
              caseStudyName
              clientTypeName
              thumbnailImage {
                childImageSharp {
                  gatsbyImageData(layout: FULL_WIDTH)
                }
                extension
                publicURL
              }
            }
          }
        }
      }
    }
  `);
  const { services, categories } = getCaseStudiesOptions();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilters, setSelectedFilters] = useState({
    categories: [],
    services: [],
  });

  const allCaseStudies = useMemo(() => {
    const caseStudiesQueries = caseStudiesQuery.allMarkdownRemark;
    return filteredCaseStudies(caseStudiesQueries, selectedFilters.categories, selectedFilters.services, searchTerm);
  }, [selectedFilters, searchTerm]);

  const { activePage, currentPage, setCurrentPage } = useActivePage(location);

  const currentCaseStudies = useMemo(() => {
    const firstPageIndex = (currentPage - 1 || 0) * pageSize;
    const lastPageIndex = firstPageIndex + pageSize;
    return allCaseStudies.slice(firstPageIndex, lastPageIndex);
  }, [currentPage, allCaseStudies]);

  const setToFirstPage = () => {
    setCurrentPage(1);
    navigate(`?page=1`);
  }

  const chooseServices = (services) => {
    setSelectedFilters({ categories: selectedFilters.categories, services: services });
    setToFirstPage();
  }

  const chooseCategory = (categories) => {
    setSelectedFilters({ services: selectedFilters.services, categories: categories });
    setToFirstPage();
  }

  const inputSearchHandler = (value) => {
    setSearchTerm(value);
    setToFirstPage();
  }

  return (
    <StandardSection
      id="case-studies-section"
      title="Case study library"
      titleChildren={
        <p>
          The case studies presents examples of our projects, how we have approached specific challenges, and most
          importantly, how our clients benefited from them.
        </p>
      }
    >
      <div className={styles.searchFilters}>
        <MultipleSelectInput
          options={services}
          onChange={chooseServices}
          values={selectedFilters.services}
          label="Services"
        />
        <MultipleSelectInput
          options={categories}
          onChange={chooseCategory}
          values={selectedFilters.categories}
          label="Categories"
        />
        <SearchInput value={searchTerm} onChange={inputSearchHandler} />
      </div>
      <div className={styles.searchResults}>
        {currentCaseStudies.map((data, index) => (
          <CaseStudyCard key={index} {...data} className={styles.singleSearchResult} />
        ))}
      </div>
      {allCaseStudies.length > 6 && (
        <PaginationControls
          totalCount={allCaseStudies.length}
          currentPage={currentPage}
          pageSize={pageSize}
          activePage={activePage}
          onPageChange={(page) => setCurrentPage(page)}
        />
      )}
    </StandardSection>
  );
}
